import {
  CaseFindingPresentationType,
  ConversationQuestionType,
  FeatureFragment,
  FindingSource,
  PatientSuggestionMiExplanationsPairMetrics,
  QualifiedFeature,
  QualifierFragment,
} from "fe-common/graphql";
import {
  SelectedSuggestionWithMatrices,
  SuspectedDiseases,
} from "fe-web-components/dialogs/SuggestionZoom";
import { WQualifiedFeature, WQualifier } from "@kahunmedical/common-wtypes";

//TODO: move to common, reuse from useConversation
type Answer = {
  type: ConversationQuestionType;
  qfs: QualifiedFeature[];
  group: CaseFindingPresentationType;
  source: FindingSource;
  skipped?: boolean;
  wasRestored?: boolean;
};

const POPULATION_FEATURE_ID = "FE.00000000-0000-0000-0000-000000000001";

export function consolidateFindingsByFeature(findings: Answer[]) {
  const flattened = findings.flatMap((f) =>
    f.qfs?.map((qf) => ({ ...f, qfs: [qf] })),
  );
  const shouldConsolidate = ({ qfs }: Answer) =>
    qfs[0].feature.id !== POPULATION_FEATURE_ID && !qfs[0].skip;
  const [consolidate, nonConsoldate] = partition2(flattened, shouldConsolidate);
  return [
    ...consolidateValidFindingsByFeature(consolidate),
    ...nonConsoldate.map((nc) => nc.qfs[0]),
  ];
}

function partition2<T>(array: T[], predicate: (T) => any): T[][] {
  return array.reduce(
    (acc, item) =>
      predicate(item) ? (acc[0].push(item), acc) : (acc[1].push(item), acc),
    [[], []],
  );
}

export function consolidateValidFindingsByFeature(findings: Answer[]) {
  // group by feature+background/active+skip
  // for population features, also include the relation
  const groupby = ({ qfs: [f], group }: Answer) =>
    `${f.feature.id}${group !== CaseFindingPresentationType.BackgroundFinding}`;

  // given a group, create a single QF which is the root for this group (qualifiers will be added afterwards)
  const rootQFForGroup = (qfs: QualifiedFeature[]) => {
    const qf: QualifiedFeature = {
      ...qfs[0],
      feature: qfs[0].feature,
      qualifiers: [],
      skip: qfs[0].skip,
    };
    return qf;
  };

  return [...arrayGroupByKey(findings, groupby).values()]
    .map((as) => {
      const qfs = as.map((a) => a.qfs[0]);

      if (qfs.length === 1) return qfs[0];
      //There is a negated QF with no qualifiers, ignore all qualifiers
      //since {feature: f, negate: true} contains all {feature: f, negate: true, qualifiers: [q]} for each q
      if (qfs.some((qf) => qf.negate && !qf.qualifiers.length)) {
        return {
          ...rootQFForGroup(qfs),
          negate: true,
          qualifiers: [],
        };
      }

      // if all qfs are negated, don't group
      if (qfs.every((qf) => qf.negate)) {
        return qfs;
      }

      // if we have length>1 with the same feature....
      return qfs.reduce((root, qf) => {
        // add all qualifiers to a root qf...
        if (qf.qualifiers?.length) {
          root.qualifiers.push(
            ...qf.qualifiers.map((q) =>
              // if qf is negated, move the negate into the qualifier
              qf.negate ? { ...q, negate: true } : q,
            ),
          );
        }
        return root;
      }, rootQFForGroup(qfs));
    })
    .flat();
}
export function arrayGroupByKey<T, G>(
  array: T[],
  keyMapper: (t: T) => G,
  compareFn?: (a: T, b: T) => number,
): Map<G, T[]> {
  const map: Map<G, T[]> = array.reduce((acc, v) => {
    const key: G = keyMapper(v);
    acc.set(key, acc.get(key) || []);
    acc.get(key).push(v);
    return acc;
  }, new Map());

  if (compareFn) {
    for (const k of map.keys()) map.set(k, map.get(k).sort(compareFn));
  }

  return map;
}

export interface DiseaseMatrices
  extends PatientSuggestionMiExplanationsPairMetrics {
  index: number;
  diseaseName: string;
  diseaseId: string;
  prob: number;
  postIfTrue: number;
  postIfFalse: number;
  specificity: number;
  crId: string;
}

// extract the list of all diseases, with metrics for each disease in relation to the suggestion
export function extractMetricsForSuggestionAndAllDiseases(
  diseases: SuspectedDiseases[],
  suggestion: SelectedSuggestionWithMatrices,
): DiseaseMatrices[] {
  // metrics related to one disease<->suggestion
  const metricsOfDisease = (
    d: SuspectedDiseases,
    index: number,
  ): DiseaseMatrices => {
    if (!d) return null;
    const prob = d.prob;
    const suggestionMetric = suggestion?.perDiseaseMetrics
      ?.filter((pdm) => pdm.diseaseIndex === index)
      .find(
        (pdm) =>
          !suggestion.bestSubtype ||
          pdm.suggestion?.id === suggestion.bestSubtype?.id,
      );
    const fGD = suggestionMetric?.findingGivenDisease || suggestion.findingLeak;
    const postIfTrue = fGD * prob;
    const postIfFalse = (1 - fGD) * prob;
    return {
      index,
      diseaseName: d.name,
      diseaseId: d.id,
      ...(suggestionMetric?.pairMetrics ||
        ({} as PatientSuggestionMiExplanationsPairMetrics)),
      prob,
      postIfTrue,
      postIfFalse,
      specificity: suggestionMetric?.specificity,
      crId: d.crId,
    };
  };

  const diseaseMetrics = diseases.map(metricsOfDisease).filter((d) => d);

  if (!diseaseMetrics?.length) return [];

  // normalize
  const sumTotal = (field: "postIfTrue" | "postIfFalse") =>
    diseaseMetrics?.map((a) => a[field])?.reduce((a, b) => a + b);

  // console.log(diseaseMetrics);

  const sumIfTrue = sumTotal("postIfTrue");

  const sumIfFalse = sumTotal("postIfFalse");

  diseaseMetrics.forEach((dm) => {
    dm.postIfTrue = dm.postIfTrue / sumIfTrue;
    dm.postIfFalse = dm.postIfFalse / sumIfFalse;
  });

  return diseaseMetrics;
}

export function qfOfSuggestion(s: SelectedSuggestionWithMatrices) {
  return s?.bestSubtype || s?.suggestion;
}

export function createReferenceLinkForCrs({
  disease,
  feature,
  qualifiers,
}: {
  disease: string;
  feature: FeatureFragment;
  qualifiers: QualifierFragment[];
}) {
  const wqf = new WQualifiedFeature(
    { id: feature?.id } as any,
    qualifiers?.map(
      (q) =>
        ({
          relation: { id: q.relation?.id } as any,
          feature: q.value && ({ id: q.value?.id } as any),
        } as WQualifier),
    ),
  );
  const json = encodeURIComponent(JSON.stringify(wqf));
  const url = `https://editor-dev.kahun.com/#/w-kahun/dv/${disease}/Search?searchFinding=${json}`;
  return url;
}

export function createQfFromDiseaseMatrices(
  diseaseId: string,
  diseaseName?: string,
): QualifiedFeature {
  const qFeature = {
    id: diseaseId,
    name: diseaseName || "",
    negate: false,
    skip: false,
    feature: { id: diseaseId },
    qualifiers: [],
    mandatory: null,
  } as QualifiedFeature;
  return qFeature;
}
